import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBookOpen,
  FaExpand,
  FaHistory,
  FaTimes,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { getSettings } from "../../../services/settingsServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const stripHtml = (html = "") => String(html || "").replace(/<[^>]*>/g, "");

const useLockBody = (locked) => {
  useEffect(() => {
    if (!locked) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [locked]);
};

const ImageModal = ({ src, alt, onClose }) => {
  useLockBody(Boolean(src));

  useEffect(() => {
    if (!src) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [src, onClose]);

  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Image agrandie"
      onClick={onClose}
    >
      <div
        className="relative max-h-[92vh] max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 p-2 shadow-2xl backdrop-blur-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={src}
          alt={alt || "Histoire du club"}
          className="max-h-[86vh] w-full rounded-[1.5rem] object-contain"
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>
      </div>
    </div>
  );
};

export default function ClubHistory() {
  const [history, setHistory] = useState({
    photoUrl1: "",
    photoUrl2: "",
    photoUrl3: "",
    title: "",
    content: "",
  });

  const [current, setCurrent] = useState(0);
  const [openImage, setOpenImage] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const settings = await getSettings();
        const clubSettings = Array.isArray(settings) ? settings[0] : settings;

        if (!mounted || !clubSettings) return;

        setHistory({
          photoUrl1: clubSettings.historique_photo_url || "",
          photoUrl2: clubSettings.historique_photo2_url || "",
          photoUrl3: clubSettings.historique_photo3_url || "",
          title: clubSettings.historique_title || "L’histoire du club",
          content: clubSettings.historique_content || "",
        });
      } catch (error) {
        console.error("Erreur lors du chargement de l’histoire du club :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const images = useMemo(() => {
    return [history.photoUrl1, history.photoUrl2, history.photoUrl3]
      .map(getImageUrl)
      .filter(Boolean);
  }, [history.photoUrl1, history.photoUrl2, history.photoUrl3]);

  useEffect(() => {
    if (images.length <= 1) return undefined;

    const interval = setInterval(() => {
      setCurrent((index) => (index + 1) % images.length);
    }, 5500);

    return () => clearInterval(interval);
  }, [images.length]);

  const goPrevious = () => {
    setCurrent((index) => (index - 1 + images.length) % images.length);
  };

  const goNext = () => {
    setCurrent((index) => (index + 1) % images.length);
  };

  const isLong = useMemo(() => {
    return stripHtml(history.content).length > 700;
  }, [history.content]);

  return (
    <section className="relative overflow-hidden bg-white py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.10),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:42px_42px] opacity-30 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaHistory />
            Mémoire du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Historique{" "}
            <span className="text-primary">
              du club
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Découvrez les origines du club, son évolution et les moments qui ont
            marqué son histoire.
          </p>
        </Reveal>

        <Reveal variant="zoom" className="mt-14">
          <article className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
              <div className="relative min-h-[360px] overflow-hidden bg-slate-900 lg:min-h-[560px]">
                {images.length > 0 ? (
                  <>
                    {images.map((image, index) => (
                      <img
                        key={image}
                        src={image}
                        alt={history.title || "Histoire du club"}
                        className={`absolute inset-0 h-full w-full object-cover transition duration-1000 ${
                          index === current
                            ? "scale-105 opacity-100"
                            : "scale-100 opacity-0"
                        }`}
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                    ))}

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

                    <button
                      type="button"
                      onClick={() => setOpenImage(images[current])}
                      className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white shadow-lg backdrop-blur-xl transition hover:scale-105 hover:bg-white/25"
                      aria-label="Agrandir l’image"
                    >
                      <FaExpand />
                    </button>

                    {images.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={goPrevious}
                          className="absolute left-5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white shadow-lg backdrop-blur-xl transition hover:scale-105 hover:bg-white/25"
                          aria-label="Image précédente"
                        >
                          <FaArrowLeft />
                        </button>

                        <button
                          type="button"
                          onClick={goNext}
                          className="absolute right-5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white shadow-lg backdrop-blur-xl transition hover:scale-105 hover:bg-white/25"
                          aria-label="Image suivante"
                        >
                          <FaArrowRight />
                        </button>

                        <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2">
                          {images.map((_, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => setCurrent(index)}
                              className={`h-2.5 rounded-full transition ${
                                index === current
                                  ? "w-8 bg-primary"
                                  : "w-2.5 bg-white/50"
                              }`}
                              aria-label={`Afficher l’image ${index + 1}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="flex h-full min-h-[360px] items-center justify-center bg-gradient-to-br from-slate-900 to-slate-950 text-white">
                    <FaBookOpen className="text-6xl opacity-40" />
                  </div>
                )}
              </div>

              <div className="p-6 sm:p-8 lg:p-10">
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-800 dark:bg-orange-500/10 dark:text-orange-300">
                  <FaBookOpen />
                  Notre histoire
                </div>

                <h3 className="font-barlow text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl">
                  {history.title || "L’histoire du club"}
                </h3>

                {history.content ? (
                  <div
                    className={`mt-6 text-base leading-8 text-slate-600 dark:text-slate-300 [&_a]:font-bold [&_a]:text-primary [&_p]:mb-4 [&_strong]:text-slate-950 dark:[&_strong]:text-white ${
                      isLong ? "lg:columns-2 lg:gap-8" : ""
                    }`}
                    style={
                      isLong
                        ? {
                            columnRule: "1px solid rgba(148,163,184,0.25)",
                          }
                        : undefined
                    }
                    dangerouslySetInnerHTML={{
                      __html: history.content,
                    }}
                  />
                ) : (
                  <p className="mt-6 text-base leading-8 text-slate-500 dark:text-slate-400">
                    L’histoire du club sera prochainement disponible.
                  </p>
                )}
              </div>
            </div>
          </article>
        </Reveal>
      </div>

      <ImageModal
        src={openImage}
        alt={history.title}
        onClose={() => setOpenImage("")}
      />
    </section>
  );
}